import { useMemo } from 'react';
import useSelectedTreeItem, {
  TreeNodeRef,
} from '@elementary/common-ui/hooks/useSelectedTreeItem';
import { getTreeNodeAssetIds } from '@elementary/common-ui/hooks/treeData/utils';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { Test, TestManagementGqlTestsResponse } from 'shared/types';
import { normalizeTest } from 'testManagement/utils/utils';
import useTestManagementTests from 'shared/hooks/useTestManagementTests';

type Props = {
  includeAlertDestinations?: boolean;
  ignoreTreeView?: boolean;
};

const useTestManagementTableData = ({
  includeAlertDestinations,
  ignoreTreeView,
}: Props = {}): {
  data: Test[];
  isLoading: boolean;
  filterCounters: Record<string, number>;
} => {
  const { urlTreeNode, urlSavedView } = useTreeViewParams();

  const { data: testsData = [], isLoading } = useTestManagementTests<Test[]>({
    includeAlertDestinations: !!includeAlertDestinations,
    select: (data: TestManagementGqlTestsResponse) =>
      (data?.tests ?? [])
        .filter(
          (test) => test.sourceType !== 'elementary_cloud' || !!test.tableAsset,
        )
        .map(normalizeTest),
  });

  const { selectedTreeFolderNodes, selectedTreeNode } = useSelectedTreeItem();

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.flatMap(getTreeNodeAssetIds);
    }

    if (selectedTreeNode) {
      return getTreeNodeAssetIds(selectedTreeNode);
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const testsByTreeView = useMemo(() => {
    if (ignoreTreeView || !selectedTreeNodeIds) {
      return testsData.sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeNode?.resourceType === 'test') {
      return testsData
        .filter((test) => test.id === selectedTreeNode?.id)
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeFolderNodes?.length) {
      return testsData
        .filter((test) =>
          selectedTreeFolderNodes?.some((node: TreeNodeRef) =>
            node.resourceType === 'test'
              ? node.id === test.id
              : !!test.asset_id &&
                getTreeNodeAssetIds(node).includes(test.asset_id),
          ),
        )
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    if (selectedTreeNode) {
      return testsData
        .filter(
          (test) =>
            !!test.asset_id &&
            getTreeNodeAssetIds(selectedTreeNode).includes(test.asset_id),
        )
        .sort((a: Test, b: Test) => a.id?.localeCompare(b.id));
    }

    return [];
  }, [
    selectedTreeNode,
    testsData,
    selectedTreeFolderNodes,
    selectedTreeNodeIds,
    ignoreTreeView,
  ]);

  const filterCounters = useMemo(() => {
    const counterMap = {
      all: testsByTreeView.length,
      singular: 0,
      column: 0,
      table: 0,
    };

    testsByTreeView.forEach((item) => {
      if (item.is_custom_sql) {
        counterMap.singular += 1;
      } else if (item.column) {
        counterMap.column += 1;
      } else {
        counterMap.table += 1;
      }
    });

    return counterMap;
  }, [testsByTreeView]);

  return {
    data: testsByTreeView,
    isLoading,
    filterCounters,
  };
};

export default useTestManagementTableData;
